import { useMemo } from "react";
import Markdown from "react-markdown";
import styled from "styled-components";

import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import FlexBox from "../FlexBox";
import {
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  PRIMARY_800,
  white,
} from "../colors";
import tickIcon from "../../../assets/tick.svg";

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const MessageBubbleContainer = styled(FlexBox)`
  flex-direction: column;
  border-radius: 0.75rem;
  border: 1px solid ${DAVYS_GREY_400};
  box-sizing: border-box;

  ${({ sender }) =>
    sender === "ai"
      ? `
    overflow: visible;
    width: 100%;
    align-self: stretch;
    max-width: 100%;
    background: ${white};
  `
      : `
    overflow: hidden;
    align-self: flex-end;
    width: max-content;
    max-width: 17rem;
    background: #F7F7F7;
  `}
`;

const MessageBubbleTop = styled(FlexBox)`
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  background: ${DAVYS_GREY_200};
  padding: 0.75rem 1rem;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  box-sizing: border-box;
`;

const ClientLabel = styled.span`
  color: ${DAVYS_GREY_700};
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
`;

const ClientName = styled.span`
  color: ${DAVYS_GREY_800};
  font-size: 0.875rem;
  font-weight: 500;
`;

const ClientId = styled.span`
  color: ${DAVYS_GREY_800};
  font-size: 0.875rem;
  font-weight: 500;
`;

const MessageBubbleBody = styled.div`
  padding: 1rem;
  color: ${DAVYS_GREY_800};
  font-size: 0.875rem;
  line-height: 1.5rem;
  box-sizing: border-box;

  p,
  ul,
  ol,
  pre,
  blockquote,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }

  p + p,
  p + ul,
  p + ol,
  ul + p,
  ol + p,
  ul + ul,
  ol + ol,
  pre + p,
  p + pre {
    margin-top: 0.5rem;
  }
`;

const ToolStepsWrapper = styled.div`
  margin-bottom: 0.5rem;
  box-sizing: border-box;
`;

const ToolStepsHeader = styled(FlexBox)`
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${PRIMARY_800};
  user-select: none;
`;

const ToolStepsList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const ToolStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  font-size: 0.75rem;
`;

const StepIcon = styled.div`
  width: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${PRIMARY_800};
`;

const Spinner = styled.div`
  width: 10px;
  height: 10px;
  border: 2px solid ${PRIMARY_800};
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const StepText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AiChatMessageBubble = ({
  sender = "ai",
  markdownText = "",
  clientName,
  clientCid,
  toolEvents = [],
  footer,
  children,
  showSteps = true,
  setShowSteps,
  showMessageBubble = true,
}) => {
  const hasClientMeta = sender === "ai" && (clientName || clientCid);

  const dedupedToolEvents = useMemo(() => {
    const map = new Map();

    toolEvents.forEach(event => {
      const text =
        event?.summary ||
        event?.label ||
        event?.message ||
        event?.tool ||
        "Processing...";

      const key = text.trim().toLowerCase();
      const isCompleted =
        event?.status === "completed" || event?.status === "done";
      const existing = map.get(key);
      const existingCompleted =
        existing?.status === "completed" || existing?.status === "done";

      if (!existing || (!existingCompleted && isCompleted)) {
        map.set(key, event);
      }
    });

    return Array.from(map.values());
  }, [toolEvents]);

  return (
    <ComponentWrapper>
      {dedupedToolEvents.length > 0 && (
        <ToolStepsWrapper>
          <ToolStepsHeader onClick={() => setShowSteps(v => !v)}>
            {showSteps ? <FiChevronDown /> : <FiChevronRight />}
            <span>Show steps</span>
          </ToolStepsHeader>

          {showSteps && (
            <ToolStepsList>
              {dedupedToolEvents.map((event, index) => {
                const text =
                  event?.summary ||
                  event?.label ||
                  event?.message ||
                  event?.tool ||
                  "Processing...";

                const isCompleted =
                  event?.status === "completed" || event?.status === "done";
                const isLastTool = dedupedToolEvents?.length - 1 === index;

                return (
                  <ToolStepRow key={`${text}-${index}`}>
                    <StepIcon>
                      {isCompleted || !isLastTool ? (
                        <img
                          src={tickIcon}
                          alt="Completed"
                          height={12}
                          width={12}
                        />
                      ) : (
                        <Spinner />
                      )}
                    </StepIcon>

                    <StepText>{text}</StepText>
                  </ToolStepRow>
                );
              })}
            </ToolStepsList>
          )}
        </ToolStepsWrapper>
      )}

      {showMessageBubble && (
        <MessageBubbleContainer sender={sender}>
          {hasClientMeta && (
            <MessageBubbleTop>
              <ClientLabel>CLIENT:</ClientLabel>
              {clientName && <ClientName>{clientName}</ClientName>}
              <ClientId>
                {clientName && "/ "}#{clientCid}
              </ClientId>
            </MessageBubbleTop>
          )}

          <MessageBubbleBody>
            {children || <Markdown>{markdownText}</Markdown>}
          </MessageBubbleBody>

          {footer}
        </MessageBubbleContainer>
      )}
    </ComponentWrapper>
  );
};

export default AiChatMessageBubble;
